import { useSyncExternalStore } from 'react';

/**
 * Which view mode each matrix is displayed in — normal or global
 * (fit-to-screen, read-only). See the 2026-08-25 design doc, S1-S4.
 *
 * Per-viewer and per-matrix, in localStorage. Deliberately NOT in
 * `matrices.viewState`: that is server-persisted and shared across every
 * viewer, so one person toggling global view would yank everyone else into
 * it — and since the fit factor is computed from each viewer's own box, a
 * shared boolean would not even produce a shared picture.
 *
 * Only the non-default value is stored: switching back to `normal` removes
 * the key rather than writing it. Entries therefore exist only for matrices
 * the user has deliberately left in global view, so the set self-prunes and
 * needs no LRU, TTL or cleanup pass. The residual case — a user who leaves
 * many matrices in global view and never returns — is ~30 bytes per matrix
 * against a ~5MB budget, and is accepted rather than engineered against.
 *
 * Separate from `tweak-store.ts` on purpose: that module declares itself the
 * only owner of settings-page *tweak* keys, and this is a user-facing view
 * preference rather than a tweak (design doc T4 — the toolbar toggle is
 * already the opt-in, so a tweak would gate a gate).
 */

export type MatrixViewMode = 'normal' | 'global';

const DEFAULT_MODE: MatrixViewMode = 'normal';

/** Exported for tests — the storage key for one matrix's view mode. */
export function matrixViewModeStorageKey(matrixId: string): string {
  return `matrix.${matrixId}.viewMode`;
}

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    // localStorage may be unavailable (private mode, SSR) — degrade silently.
    return null;
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (): void => listener();
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
}

/** The stored mode for one matrix, or `normal` when unset or unrecognised. */
export function readMatrixViewMode(matrixId: string): MatrixViewMode {
  return readRaw(matrixViewModeStorageKey(matrixId)) === 'global' ? 'global' : DEFAULT_MODE;
}

/** Persist a matrix's view mode and notify subscribers. `normal` deletes the key. */
export function setMatrixViewMode(matrixId: string, mode: MatrixViewMode): void {
  const key = matrixViewModeStorageKey(matrixId);
  try {
    if (mode === 'global') {
      globalThis.localStorage?.setItem(key, 'global');
    } else {
      globalThis.localStorage?.removeItem(key);
    }
  } catch {
    // Unavailable storage — the mode still applies for this render, it just
    // will not survive a reload. Degrading silently beats failing the toggle.
  }
  emit();
}

/** Subscribe to one matrix's view mode. */
export function useMatrixViewMode(matrixId: string): MatrixViewMode {
  return useSyncExternalStore(
    subscribe,
    () => readMatrixViewMode(matrixId),
    () => DEFAULT_MODE,
  );
}
